import { MaritalStatus, UpdateProfileStatus, useCustomerDetails, useUpdateFinancialInfo } from '@egw/api'
import { Button, Field, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { useEffect, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FinancialField } from '../../types'

const log = new Logger('Edit Marital Status')

interface MaritalStatusFormInterface {
  [FinancialField.MARITAL_STATUS]: MaritalStatus
}

export const EditMaritalStatus: FC = () => {
  const { t } = useTranslation('features-settings')
  const { isOpen, openModal, closeModal } = useModalState()
  const [updateError, setUpdateError] = useState<boolean>(false)

  const { data, refetch, isRefetching } = useCustomerDetails()
  const value = data?.customer.maritalStatus

  const { mutate: updateFinancialInfo, isLoading } = useUpdateFinancialInfo({
    onSuccess: (res) => {
      if (res.__typename === 'UpdateProfileError' || res.status === UpdateProfileStatus.FAILED) {
        log.error('Error updating tax filing field', { data })
        setUpdateError(true)
      } else if (res.status === UpdateProfileStatus.COMPLETE) {
        void refetch()
          .catch((error: unknown) => {
            log.error(error)
          })
          .finally(() => {
            setUpdateError(false)
            closeModal()
            reset()
          })
      }
    },
    onError: () => {
      setUpdateError(true)
    },
  })

  const defaultValues = {
    [FinancialField.MARITAL_STATUS]: value,
  }

  const { handleSubmit, control, reset, formState } = useForm<MaritalStatusFormInterface>({
    mode: 'all',
    defaultValues,
  })

  useEffect(() => {
    if (data) {
      reset({
        [FinancialField.MARITAL_STATUS]: value,
      })
    }
  }, [data, reset, value])

  const onSubmit = (formData: MaritalStatusFormInterface): void => {
    setUpdateError(false)
    log.debug('Submitting Form', { formData })
    void updateFinancialInfo(formData)
  }

  return data ? (
    <>
      <Field
        className="w-full justify-between border-list mb-0 py-4 px-2"
        valueClassName="text-text-meta typo-primary-base-light"
        value={t('personal_info.marital_status')}
        rightValue={value ? t(`marital_status.${value}`) : t('personal_info.not_specified')}
        testId="editMaritalStatus"
        onEdit={openModal}
      />
      <ModalDrawer isOpen={isOpen} onClose={closeModal} title={t('personal_info.edit_marital_status')}>
        <form
          id="updateMaritalStatusForm"
          className="flex flex-col gap-4 h-full md:h-screen justify-between"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(onSubmit)()
          }}
        >
          <div className="flex flex-col gap-10">
            <div>
              <Controller
                name="maritalStatus"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { ref: _, ...restField } }) => (
                  <RadioGroup
                    {...restField}
                    options={Object.values(MaritalStatus).map((item) => ({
                      value: item,
                      label: t(`marital_status.${item}`),
                    }))}
                    name="maritalStatus"
                    className="pb-md typo-primary-base"
                    testId="marital-status-radio"
                  />
                )}
              />
            </div>
            {updateError ? (
              <div className="flex bg-alertErrorBg p-3 rounded-md">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{t('common.update_error')}</p>
              </div>
            ) : null}
          </div>
          <Button
            id="updateMaritalStatus"
            type="submit"
            isLoading={isLoading || isRefetching}
            isDisabled={!formState.isValid}
          >
            {t('common.confirm')}
          </Button>
        </form>
      </ModalDrawer>
    </>
  ) : null
}
